/**
 * What needs the analyst today, in three lanes.
 *
 * The landing page used to report on the PLATFORM — how many organisations and
 * tenants exist, a "-100%" delta, the active tenant's raw id — beside a live feed
 * of all cross-chain activity. None of that is anybody's work. An investigator
 * opens this product to answer one question: what happened since I last looked,
 * and what is waiting on me. These are the three reads that can answer it from
 * what the platform already stores.
 *
 * Each lane answers with a {@link ListOutcome}, never a bare array: on a
 * forensics surface "nothing fired" and "we could not ask" must not look the
 * same, and a queue that silently reads empty is the most expensive version of
 * that mistake — the analyst goes home.
 *
 * Each lane is also PERMISSION-GATED exactly as the nav entry for the same
 * surface is (`unavailable`): a reader without `alerts:read` is not shown an
 * alerts lane that can only ever be empty for them. The verb matters — these
 * families mint `:read`, and `:list` would be a gate that never passes, so the
 * lane would be invisible to everyone. `config/scopeGates.test.ts` is the guard
 * that caught exactly that.
 */
import * as React from "react";
import { useAlertHistory, useAlertRules, alertCriticality, alertTimestampMs, alertTxHash } from "@authe/alerts";
import { usePermissions } from "@authe/ui-core/permissions";
import { useQuery } from "@authe/ui-core/api";
import { queryData } from "@authe/ui-core";
import type { EventCriticality } from "@authe/fe-commons/events";
import { walkPages } from "@/api/pagedList";
import { listOutcome, type ListOutcome } from "@/api/readOutcome";
import { useAlertsSource, useInvestigationsSource } from "@/api/adapters";
import { RESOURCES } from "@/config";
import {
  normalizeWatches,
  watchChain,
  watchStatus,
  type WatchItem,
} from "@/features/watches/model";
import type { Investigation } from "@/api/investigationsSource";
import {
  LANE_SIZE,
  queueCases,
  transactionRef,
  type QueueCase,
} from "./queueRules";

/** A lane the reader may not read at all — rendered as absent, not as empty. */
export type LaneOutcome = ListOutcome | "unavailable";

export interface QueueLane<T> {
  items: T[];
  outcome: LaneOutcome;
  /** Rows beyond the {@link LANE_SIZE} this lane shows, when the read knows. */
  more: number;
  /**
   * Why the read failed, where the read can say. A lane that only reports
   * "failed" renders generic boilerplate under its title; the reason is what
   * tells the analyst whether to retry or to call someone.
   */
  error?: unknown;
  retry: () => void;
}

const EMPTY_LANE: QueueLane<never> = {
  items: [],
  outcome: "unavailable",
  more: 0,
  retry: () => {},
};

// ── Alerts that fired ────────────────────────────────────────────────────────

export interface FiredAlert {
  id: string;
  /** The rule's name where it resolves, else its id — never a bare uuid alone. */
  rule: string;
  criticality: EventCriticality;
  severity: string | undefined;
  atMs: number;
  /** For display: what an analyst recognises. */
  txHash: string;
  /**
   * What `/transactions/$id` takes, or "" when the rule matched no single
   * transaction.
   *
   * A bare hash is ambiguous across forked chains and is NOT a canonical id, so
   * it is prefixed with the alert's chain — the same `<chain>:<hash>` ref the
   * command palette builds, which the API splits at its last colon.
   */
  txRef: string;
}

/**
 * What to call the rule that fired, when the rules list may not name it.
 *
 * An alert matters even when we cannot say which rule caught it — that is why
 * this degrades rather than hides the row. But it degrades to a *sentence*: a
 * uuid in the lead column is unreadable, and worse, it looks like a name.
 */
export function ruleLabel(
  ruleId: string | null | undefined,
  names: ReadonlyMap<string, string>,
  rulesRead: boolean,
): string {
  if (!ruleId) return "Unnamed rule";
  const name = names.get(ruleId);
  if (name) return name;
  // Read the list and the id is not in it: the rule existed when it fired and
  // has since been deleted. The alert is still a fact about the chain.
  return rulesRead ? "Deleted rule" : "Rule name unavailable";
}

export function useFiredAlerts(): QueueLane<FiredAlert> {
  const { can } = usePermissions();
  const allowed = can(RESOURCES.alerts, "read");
  const source = useAlertsSource();
  // The rules list is what turns a `rule_id` into a name. Its failure is not the
  // lane's failure: an alert that fired matters even when we cannot say which
  // rule caught it, so a failed rules read degrades to the id.
  //
  // Stubbed rather than skipped when the reader has no `alerts:list`: hooks
  // cannot be conditional, and firing a read that can only 403 would put an
  // error in the console of every restricted reader on every page load.
  const rulesSource = React.useMemo(
    () => (allowed ? source : { ...source, listRules: async () => [] }),
    [allowed, source],
  );
  const rules = useAlertRules(rulesSource);
  const history = useAlertHistory(source, { pageSize: LANE_SIZE * 4, autoLoad: allowed });

  const ruleNames = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const rule of rules.items) if (rule.id) map.set(rule.id, rule.name ?? rule.id);
    return map;
  }, [rules.items]);

  // Whether an unresolved `rule_id` means "that rule is gone" or "we have not
  // got the list yet". Both degrade, but to different words — and neither
  // degrades to the raw uuid, which is what this lane did: three rows of
  // `3aaa6c31-be48-4841-8d2e-3b7e8f99…` as the headline text of the front door.
  //
  // Seen live, and instructive: those rows were NOT deleted rules. The rules
  // read takes seconds, so the lane rendered ids for a name that was simply
  // still in flight, then swapped to "Watch: Uniswap Watch" when it landed.
  // Exactly the absence this product keeps collapsing — an id is not a name,
  // and "not yet" is not "gone".
  // `allowed &&` is load-bearing: without `alerts:list` the rules source above
  // is STUBBED to resolve empty, which reports "ready" over a list that was
  // never fetched — and every rule would then read "Deleted rule".
  const rulesRead = allowed && rules.status === "ready";

  return React.useMemo(() => {
    if (!allowed) return EMPTY_LANE;
    const sorted = [...history.events].sort((a, b) => alertTimestampMs(b) - alertTimestampMs(a));
    const items = sorted.slice(0, LANE_SIZE).map((event, index) => ({
      id: event.id ?? `${event.rule_id ?? "rule"}:${alertTimestampMs(event)}:${index}`,
      rule: ruleLabel(event.rule_id, ruleNames, rulesRead),
      criticality: alertCriticality(event),
      severity: event.severity ?? undefined,
      atMs: alertTimestampMs(event),
      txHash: alertTxHash(event),
      txRef: transactionRef(alertTxHash(event), event.chain_id ?? null),
    }));
    return {
      items,
      outcome: listOutcome({
        count: items.length,
        loading: history.status === "loading" || history.status === "idle",
        failed: history.status === "error",
      }),
      more: Math.max(0, sorted.length - items.length),
      // The history hook reports a status, not an error object, so this lane
      // has only its title to say it with.
      retry: history.refresh,
    };
  }, [allowed, history.events, history.status, history.refresh, ruleNames, rulesRead]);
}

// ── Cases with activity ──────────────────────────────────────────────────────

/** The cases lane: {@link queueCases} over the caller's list. */
export function useActiveCases(): QueueLane<QueueCase> {
  const { can } = usePermissions();
  // `:list` here, `:read` for the two lanes above — the families genuinely
  // differ, and the scope catalogue is the only authority on which. Ungated,
  // this read 403s for every reader without it and the landing page opens on a
  // permanent "Couldn't read your cases" with a Retry that cannot succeed.
  const allowed = can(RESOURCES.investigations, "list");
  const source = useInvestigationsSource();
  const query = useQuery<Investigation[]>(
    React.useCallback(async () => queryData(await source.list()), [source]),
    {
      resourcePolicy: { independent: "Watch and case workflow state." }, enabled: allowed },
  );

  return React.useMemo(() => {
    if (!allowed) return EMPTY_LANE;
    const open = (query.data ?? []).filter((c) => c.status !== "archived");
    const items = queueCases(query.data ?? []);
    return {
      items,
      outcome: listOutcome({
        count: items.length,
        loading: query.isLoading,
        failed: Boolean(query.error),
      }),
      more: Math.max(0, open.length - items.length),
      error: query.error,
      retry: query.refetch,
    };
  }, [allowed, query.data, query.isLoading, query.error, query.refetch]);
}

// ── Watches that are live ────────────────────────────────────────────────────

export interface QueueWatch {
  id: string;
  label: string;
  chain: string | undefined;
  /**
   * The shared four-state reading — Active / Paused / Not covered / On your
   * runs — not a boolean of my own. "Not covered" in particular is the state a
   * queue must show: the watch exists, is enabled, and nothing will ever fire
   * it.
   */
  status: ReturnType<typeof watchStatus>;
}

export function useLiveWatches(): QueueLane<QueueWatch> {
  const { can } = usePermissions();
  const allowed = can(RESOURCES.watches, "read");
  const query = useQuery<WatchItem[]>(
    React.useCallback(
      async (apiClient, signal) =>
        // Walked, not fetched: `/watches` applies `filter_viewable` AFTER taking
        // one page, so a fetch can come back short for reasons that have nothing
        // to do with how many watches exist. Same reasoning as the Watches page.
        queryData(normalizeWatches({ items: await walkPages(apiClient, "/watches", { signal }) })),
      [],
    ),
    {
      resourcePolicy: { independent: "Watch and case workflow state." }, enabled: allowed },
  );

  return React.useMemo(() => {
    if (!allowed) return EMPTY_LANE;
    const all = query.data ?? [];
    const items = all.slice(0, LANE_SIZE).map((watch) => ({
      id: watch.id,
      label: watch.name || watch.target || watch.id,
      chain: watchChain(watch),
      status: watchStatus(watch),
    }));
    return {
      items,
      outcome: listOutcome({
        count: items.length,
        loading: query.isLoading,
        failed: Boolean(query.error),
      }),
      more: Math.max(0, all.length - items.length),
      error: query.error,
      retry: query.refetch,
    };
  }, [allowed, query.data, query.isLoading, query.error, query.refetch]);
}
